import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { LayoutGrid, Sparkles, Shuffle, Code2, Hammer } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { heroSlideStrings } from "@/lib/i18n/heroSlides";
import { SITE_FACTS } from "@shared/siteFacts";
import { PROFESSIONAL_TRIAL_LIMIT, PREMIUM_TRIAL_LIMIT } from "@shared/tiers";
import { PREMIUM_APPS } from "@/lib/tiers";
import { MAP_H, MAP_W, worldDots } from "./heroMap";
import "./heroSlides.css";

/**
 * Home-page slide panel (replaces the old "used on this device" tracker). Six framed cards that auto-advance in a
 * loop and never stop on their own: about 4.5 s each, 9 s for the first (its animation is longer). Dots jump to a
 * slide; Pause stops it; keyboard focus inside the panel holds it; visitors who ask for reduced motion get it paused
 * with no animation. No network, no third-party assets: everything is inline SVG and CSS.
 * Numbers come from SITE_FACTS and the tier constants, never typed here.
 */
const SLIDE_MS = 4500;
const SLIDE_COUNT = 6;
const ACCENTS = ["#4dffc3", "#ffd166", "#b6ff3b", "#4dffc3", "#ffc857", "#ff4d2a"];
const durationFor = (i: number) => (i === 0 ? SLIDE_MS * 2 : SLIDE_MS);

function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const on = () => setReduce(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
}

/** Counts up to `to` each time `active` turns true; shows the final number straight away without motion. */
function CountUp({ to, suffix = "", active, reduce }: { to: number; suffix?: string; active: boolean; reduce: boolean }) {
  const [v, setV] = useState(to);
  useEffect(() => {
    if (!active || reduce) { setV(to); return; }
    let raf = 0;
    const t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / 1100);
      setV(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, to, reduce]);
  return <>{v.toLocaleString("en-US")}{suffix}</>;
}

const num = (s: string) => parseInt(s.replace(/[^\d]/g, ""), 10) || 0;
const fill = (s: string, vars: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));

const Svg = ({ children }: { children: React.ReactNode }) => <svg viewBox="0 0 24 24" aria-hidden="true">{children}</svg>;
const ICONS = {
  tools: <Svg><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></Svg>,
  premium: <Svg><path d="M12 3l2.2 5.8L20 11l-5.8 2.2L12 19l-2.2-5.8L4 11l5.8-2.2z" /></Svg>,
  workflow: <Svg><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></Svg>,
  api: <Svg><path d="M9 7L4 12l5 5M15 7l5 5-5 5M13 5l-2 14" /></Svg>,
  custom: <Svg><path d="M14 6l4 4-8 8H6v-4zM13 7l4 4" /></Svg>,
};

export function HeroSlides() {
  const { locale, localizedHref } = useLocale();
  const t = heroSlideStrings(locale);
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0); // bumps on every jump so the active dot's timer fill restarts
  const [userPaused, setUserPaused] = useState(false);
  const [focusHeld, setFocusHeld] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const running = !reduce && !userPaused && !focusHeld;

  useEffect(() => {
    if (!running) return;
    const id = window.setTimeout(() => setIndex(i => (i + 1) % SLIDE_COUNT), durationFor(index));
    return () => window.clearTimeout(id);
  }, [running, index, cycle]);

  const go = useCallback((n: number) => { setIndex(n); setCycle(c => c + 1); }, []);
  const { dots, cities } = useMemo(() => worldDots(), []);

  const apiN = num(SITE_FACTS.apiCount), mcpN = num(SITE_FACTS.mcpCount);
  const premiumCount = SITE_FACTS.premiumApplicationCount;
  const on = (n: number) => (index === n ? " on" : "");
  const panelVars = { ["--dur" as string]: `${durationFor(index)}ms`, ["--ac" as string]: ACCENTS[index] } as React.CSSProperties;

  // Windows that get sucked into "One place." (slide 1). dx/dy are the pull toward the centre of the scene.
  const wins: { L: number; T: number; r: number; url: string; body: React.ReactNode; dx: number; dy: number }[] = [
    { L: 0, T: 0, r: -6, url: "pdf-tool-one.com", dx: 0, dy: 0, body: <><div className="sk" style={{ width: "80%" }} /><div className="sk" style={{ width: "55%" }} /><div className="btn">{t.s1Win1}</div></> },
    { L: 61, T: 2, r: 5, url: "image-fixer.net", dx: 0, dy: 0, body: <><div className="sk" style={{ width: "70%" }} /><div className="sk" style={{ width: "90%" }} /><div className="btn y">{t.s1Win2}</div></> },
    { L: -1, T: 40, r: 4, url: "resize-it.app", dx: 0, dy: 0, body: <><div className="sk" style={{ width: "60%" }} /><div className="btn r">{t.s1Win3}</div></> },
    { L: 62, T: 42, r: -5, url: "invoice-maker.io", dx: 0, dy: 0, body: <><div className="lock">{t.s1Win4a}</div><div className="btn r">{t.s1Win4b}</div></> },
    { L: 30, T: 76, r: -2, url: "merge-files.com", dx: 0, dy: 0, body: <><div className="lock">{t.s1Win5}</div><div className="prog"><i /></div></> },
    { L: 30, T: 24, r: 3, url: "login.docs-suite.com", dx: 0, dy: 0, body: <><div className="sk" style={{ width: "50%" }} /><div className="sk" style={{ width: "80%" }} /><div className="btn y">{t.s1Win6}</div></> },
  ].map(w => ({ ...w, dx: Math.round((44 - ((w.L / 100) * 88 + 0.37 * 44)) * 10) / 10, dy: Math.round((40 - ((w.T / 100) * 80 + 11)) * 10) / 10 }));
  const tags = [["$9.99", 34, 2, "", -14], ["$14/mo", 52, 36, "r", 8], ["$29", 72, 24, "", 4], ["$19/mo", 10, 26, "r", -6]] as const;

  const cards = [
    { c: "#4dffc3", icon: ICONS.tools, n: SITE_FACTS.professionalToolCount, title: t.cardTools, desc: t.cardToolsDesc, href: "/professional-tools" },
    { c: "#b79bff", icon: ICONS.premium, n: premiumCount, title: t.cardPremium, desc: t.cardPremiumDesc, href: "/premium-apps" },
    { c: "#ffb36b", icon: ICONS.workflow, n: "", title: t.cardWorkflow, desc: t.cardWorkflowDesc, href: "/workflow-products" },
    { c: "#8f8fff", icon: ICONS.api, n: SITE_FACTS.apiCount, title: t.cardApi, desc: t.cardApiDesc, href: "/developers" },
    { c: "#ff7a9c", icon: ICONS.custom, n: "", title: t.cardCustom, desc: t.cardCustomDesc, href: "/custom-software", wide: true },
  ];

  const steps = [
    { icon: ICONS.tools, n: SITE_FACTS.professionalToolCount, d: t.s2Tools },
    { icon: ICONS.premium, n: premiumCount, d: t.s2Premium },
    { icon: ICONS.workflow, n: t.s2WorkflowWord, d: t.s2Workflow },
    { icon: ICONS.api, n: SITE_FACTS.apiCount, d: fill(t.s2Api, { mcp: SITE_FACTS.mcpCount }) },
    { icon: ICONS.custom, n: t.s2CustomWord, d: t.s2Custom },
  ];

  return (
    <div
      ref={rootRef}
      className="hs"
      onFocusCapture={e => { if ((e.target as HTMLElement).matches?.(":focus-visible")) setFocusHeld(true); }}
      onBlurCapture={() => setFocusHeld(false)}
    >
      <div className="frame">
        <div className={`panel${running ? " playing" : ""}`} style={panelVars} role="region" aria-roledescription="carousel" aria-label={t.region}>
          <div className="stage">
            <div className="deck d2" aria-hidden="true" />
            <div className="deck d1" aria-hidden="true" />

            {/* 1: problem -> solution */}
            <article className={`slide hs-s1${on(0)}`} data-k="2" aria-hidden={index !== 0} aria-label={`1 / ${SLIDE_COUNT}`}>
              <div className="glowr" /><div className="glowm" />
              <div className="eyebrow" style={{ position: "relative", zIndex: 2 }}>{t.s1Eyebrow}</div>
              <h2><span className="a">{t.s1Old}</span><span className="b">{t.s1New}</span></h2>
              <div className="art p1">
                <div className="scene">
                  {wins.map((w, i) => (
                    <div key={w.url} className="win" aria-hidden="true" style={{ left: `${w.L}%`, top: `${w.T}%`, ["--r" as string]: `${w.r}deg`, ["--i" as string]: i, ["--dx" as string]: `${w.dx}cqw`, ["--dy" as string]: `${w.dy}cqw` } as React.CSSProperties}>
                      <div className="bar"><b /><b /><b /><u>{w.url}</u></div><div className="bd">{w.body}</div>
                    </div>
                  ))}
                  {tags.map(([txt, L, T, c, r], j) => (
                    <span key={txt} className={`tag ${c}`} aria-hidden="true" style={{ left: `${L}%`, top: `${T}%`, ["--r" as string]: `${r}deg`, ["--i" as string]: j + 6, ["--dx" as string]: `${Math.round((44 - ((L / 100) * 88 + 5.3)) * 10) / 10}cqw`, ["--dy" as string]: `${Math.round((40 - ((T / 100) * 80 + 2.5)) * 10) / 10}cqw` } as React.CSSProperties}>{txt}</span>
                  ))}
                  <div className="bento">
                    {cards.map((c, i) => (
                      <Link key={c.href} href={localizedHref(c.href)} className={`bc${c.wide ? " w" : ""}`} style={{ ["--c" as string]: c.c, ["--i" as string]: i } as React.CSSProperties}>
                        {c.wide ? (
                          <><div className="ic">{c.icon}</div><div className="tx"><b>{c.title}</b><span>{c.desc}</span></div></>
                        ) : (
                          <><div className="top"><div className="ic">{c.icon}</div>{c.n && <div className="n">{c.n}</div>}</div><b>{c.title}</b><span>{c.desc}</span></>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            {/* 2: five ways to work */}
            <article className={`slide hs-s2${on(1)}`} aria-hidden={index !== 1} aria-label={`2 / ${SLIDE_COUNT}`}>
              <div className="eyebrow">{t.s2Eyebrow}</div>
              <h2 data-a style={{ ["--i" as string]: 0 } as React.CSSProperties}>{t.s2Title1} <em>{t.s2Title2}</em></h2>
              <div className="art">
                <div className="flag">{t.s2Flag}</div>
                <div className="steps">
                  {steps.map((s, i) => (
                    <div key={i} className="stp" style={{ ["--i" as string]: i } as React.CSSProperties}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">{(s.icon as any).props.children}</svg><b>{s.n}</b><span>{s.d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            {/* 3: API and MCP */}
            <article className={`slide hs-s3${on(2)}`} aria-hidden={index !== 2} aria-label={`3 / ${SLIDE_COUNT}`}>
              <div className="eyebrow">{t.s3Eyebrow}</div>
              <h2 data-a style={{ ["--i" as string]: 0 } as React.CSSProperties}>{t.s3Title1} <em>{t.s3Title2}</em></h2>
              <div className="big" data-a style={{ ["--i" as string]: 1 } as React.CSSProperties}>
                <div><b><CountUp to={apiN} suffix="+" active={index === 2} reduce={reduce} /></b><span>{t.s3Apis}</span></div>
                <div><b><CountUp to={mcpN} suffix="+" active={index === 2} reduce={reduce} /></b><span>{t.s3Mcp}</span></div>
              </div>
              <div className="term" data-a style={{ ["--i" as string]: 2 } as React.CSSProperties} aria-hidden="true">
                <div className="bar"><b /><b /><b />&nbsp;{t.s3Terminal}</div>
                <div className="bd">
                  <div className="ln" style={{ ["--i" as string]: 0 } as React.CSSProperties}><span className="ok">$</span> call pdf_merge</div>
                  <div className="ln dim" style={{ ["--i" as string]: 1 } as React.CSSProperties}>  ✓ 3 files merged</div>
                  <div className="ln" style={{ ["--i" as string]: 2 } as React.CSSProperties}><span className="ok">$</span> call calculate_loan</div>
                  <div className="ln dim" style={{ ["--i" as string]: 3 } as React.CSSProperties}>  ✓ payment schedule ready</div>
                  <div className="ln" style={{ ["--i" as string]: 4 } as React.CSSProperties}><span className="ok">$</span> call image_resize <span className="cur" /></div>
                </div>
              </div>
              <div className="chips" data-a style={{ ["--i" as string]: 3 } as React.CSSProperties}>
                <span>{t.s3Chip1}</span><span>{t.s3Chip2}</span><span>{t.s3Chip3}</span><span>{fill(t.s3Chip4, {})}</span>
              </div>
            </article>

            {/* 4: trust */}
            <article className={`slide hs-s4${on(3)}`} aria-hidden={index !== 3} aria-label={`4 / ${SLIDE_COUNT}`}>
              <div className="eyebrow">{t.s4Eyebrow}</div>
              <h2 data-a style={{ ["--i" as string]: 0 } as React.CSSProperties}>{t.s4Title1} <em>{t.s4Title2}</em></h2>
              <div className="art" data-a style={{ ["--i" as string]: 1 } as React.CSSProperties}>
                <svg className="tv" viewBox="0 0 400 230" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                  <rect className="fl l" x="12" y="30" width="250" height="170" rx="12" strokeDasharray="5 5" />
                  <text className="t" x="24" y="50">{t.s4Browser}</text>
                  <g className="l"><path d="M40 96h38v52H40z M66 96l12 12M44 114h20M44 124h20M44 134h14" /></g>
                  <g className="l"><circle cx="150" cy="122" r="16" /><circle cx="150" cy="122" r="6" /><path d="M150 100v6M150 138v6M128 122h6M166 122h6M134 106l4 4M166 106l-4 4M134 138l4-4M166 138l-4-4" /></g>
                  <g className="l"><path d="M214 96h38v52h-38z M240 96l12 12" /><path d="M224 124l7 7 12-14" strokeWidth="2.4" /></g>
                  <path className="l" d="M82 122h44M174 122h36" strokeDasharray="3 5" />
                  <circle r="4" fill="#4dffc3"><animateMotion dur="3s" repeatCount="indefinite" path="M60 124 L240 124" /></circle>
                  <g className="l r" opacity=".95"><path d="M330 78a18 18 0 0 1 4-34 24 24 0 0 1 46 6 15 15 0 0 1-2 28z" stroke="#ff6b6b" /><path d="M322 96l66-62" stroke="#ff6b6b" strokeWidth="2.6" /></g>
                  <path d="M262 118H300" stroke="#ff6b6b" strokeWidth="1.6" strokeDasharray="3 5" fill="none" /><path d="M300 108v20" stroke="#ff6b6b" strokeWidth="2.6" strokeLinecap="round" />
                  <text className="t rt" x="316" y="118">{t.s4NoUpload}</text>
                  <text className="t rt" x="316" y="130" opacity=".7">{t.s4NoCopy}</text>
                </svg>
              </div>
              <div className="badges" data-a style={{ ["--i" as string]: 2 } as React.CSSProperties}>
                <div><b>{t.s4B1}</b>{t.s4B1d}</div><div><b>{t.s4B2}</b>{t.s4B2d}</div><div><b>{t.s4B3}</b>{t.s4B3d}</div>
              </div>
            </article>

            {/* 5: who uses it */}
            <article className={`slide hs-s5${on(4)}`} aria-hidden={index !== 4} aria-label={`5 / ${SLIDE_COUNT}`}>
              <div className="eyebrow">{t.s5Eyebrow}</div>
              <div className="art" style={{ marginTop: 0 }}>
                <svg className="map" viewBox={`0 0 ${MAP_W} ${MAP_H}`} aria-hidden="true">
                  <g className="land">{dots.map(([x, y], i) => <circle key={i} cx={x} cy={y} r=".62" />)}</g>
                  {cities.map(([x, y], i) => (
                    <g key={i} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}><circle className="pl" r="1" style={{ animationDelay: `${(i * 0.35).toFixed(2)}s` }} /><circle className="pc" r="1.15" /></g>
                  ))}
                </svg>
                <div className="hn" data-a style={{ ["--i" as string]: 1 } as React.CSSProperties}>
                  <b><CountUp to={num(SITE_FACTS.usage.users)} suffix="+" active={index === 4} reduce={reduce} /></b>
                  <div className="row"><span><i>{SITE_FACTS.usage.countries}</i> {t.s5Countries}</span><span><i>{SITE_FACTS.usage.languages}</i> {t.s5Languages}</span><span>{t.s5People}</span></div>
                  <small>{t.s5Source}</small>
                </div>
              </div>
            </article>

            {/* 6: the offer */}
            <article className={`slide hs-s6${on(5)}`} aria-hidden={index !== 5} aria-label={`6 / ${SLIDE_COUNT}`}>
              <div className="eyebrow">{t.s6Eyebrow}</div>
              <h2 data-a style={{ ["--i" as string]: 0 } as React.CSSProperties}>{t.s6Title1} <em>{t.s6Title2}</em></h2>
              <Link href={localizedHref("/pricing")} className="tk a" style={{ ["--i" as string]: 1 } as React.CSSProperties}>
                <div className="pr">{PROFESSIONAL_TRIAL_LIMIT}</div>
                <div className="tx"><b>{t.s6TrialTitle}</b><span>{t.s6TrialDesc}</span><div className="pips" aria-hidden="true">{Array.from({ length: PROFESSIONAL_TRIAL_LIMIT }, (_, i) => <i key={i} />)}</div></div>
              </Link>
              <Link href={localizedHref("/pricing")} className="tk b" style={{ ["--i" as string]: 2 } as React.CSSProperties}>
                <div className="pr">{SITE_FACTS.dayPassPrice}</div><div className="tx"><b>{t.s6DayTitle}</b><span>{t.s6DayDesc}</span></div>
              </Link>
              <Link href={localizedHref("/pricing")} className="tk c" style={{ ["--i" as string]: 3 } as React.CSSProperties}>
                <div className="pr">{SITE_FACTS.monthlyPrice}</div><div className="tx"><b>{t.s6MonthTitle}</b><span>{t.s6MonthDesc}</span></div>
              </Link>
              <p className="fineo">{fill(t.s6Premium, { trials: PREMIUM_TRIAL_LIMIT, day: PREMIUM_APPS.dayPass, month: PREMIUM_APPS.monthly })}</p>
            </article>
          </div>

          <div className="foot">
            <div className="brand"><i>F</i>FreeDailyPro</div>
            <div className="dots" role="group" aria-label={t.chooseSlide}>
              {t.slideNames.map((name, n) => (
                <button key={index === n ? `${n}-${cycle}` : n} type="button" className="dot" aria-label={`${n + 1}: ${name}`} aria-current={index === n ? "true" : undefined} onClick={() => go(n)} />
              ))}
            </div>
            <button type="button" className="pp" aria-label={userPaused || reduce ? t.play : t.pause} onClick={() => setUserPaused(p => !p)}>{userPaused || reduce ? t.play : t.pause}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
